import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { customerService } from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PhoneInput, normalizePhone } from '../components/ui/phone-input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Search, RefreshCw, Tag, Trash2, Edit2, Share2, Copy,
  X, Printer, ImageOff, PackageMinus, CheckSquare, Square, AlertTriangle,
  CheckCircle2, XCircle, Clock, BadgeCheck, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtSnapTime = (iso) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' });

const fmtLakh = (n) => `Rs. ${(Math.round((Number(n) || 0) / 1000) / 100).toFixed(2)} L`;

const photoSrc = (url) => (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);
import html2canvas from 'html2canvas';
import EstimateFormDialog from '../components/EstimateFormDialog';
import GoldRateGateDialog from '../components/GoldRateGateDialog';
import useGoldRateGate from '../hooks/useGoldRateGate';
import StockEstimateCard from '../components/StockEstimateCard';

const fmt = (n) => {
  if (!n && n !== 0) return '-';
  const num = Math.round(n);
  const s = num.toString();
  const last3 = s.substring(s.length - 3);
  const rest = s.substring(0, s.length - 3);
  return 'Rs. ' + (rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' : '') + last3;
};

const StockEstimates = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const canCreate = ['admin', 'salesman'].includes(currentUser?.role);
  const isAdmin = currentUser?.role === 'admin';
  const canRemove = ['admin', 'owner', 'cashier'].includes(currentUser?.role);
  const canApprove = ['admin', 'owner'].includes(currentUser?.role);

  // Tab: 'active' | 'pending' | 'removed'
  const [activeTab, setActiveTab] = useState(() => {
    const p = searchParams.get('tab');
    if (p === 'pending' && ['admin', 'owner'].includes(currentUser?.role)) return 'pending';
    return 'active';
  });
  // Tracks the currently selected tab so async fetches can't clobber a newer tab.
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  const [pendingCount, setPendingCount] = useState(0);

  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  // Subtle background refresh indicator — shown instead of full spinner on re-loads
  const [bgRefreshing, setBgRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const stockCardRef = useRef(null);
  const isMounted = useRef(false);
  const [printingEst, setPrintingEst] = useState(null);

  // Verified tab — read-only, live-priced on tap
  const [verifiedMode, setVerifiedMode] = useState(false);
  const [verifiedLoading, setVerifiedLoading] = useState(false);
  const [photoViewUrl, setPhotoViewUrl] = useState(null);
  const [verifiedSnap, setVerifiedSnap] = useState(null);
  const verifiedPollRef = useRef(null);
  const [reverifyTags, setReverifyTags] = useState([]);
  const [sortMode, setSortMode] = useState('tag');

  // ── Per-tab in-memory cache ──
  // Tab switches within TTL are instant — no network call, no spinner.
  const CACHE_TTL = 60_000; // 60 seconds
  const tabCache = useRef({ active: [], pending: [], removed: [], verified: [] });
  const tabCacheTime = useRef({ active: 0, pending: 0, removed: 0, verified: 0 });

  const invalidateTab = (...tabs) => {
    tabs.forEach(t => { tabCacheTime.current[t] = 0; });
  };

  // Multi-select mode (active tab only)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Remove dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeItems, setRemoveItems] = useState([]);
  const [removing, setRemoving] = useState(false);

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Filters (active tab)
  const [filterGroup, setFilterGroup] = useState(searchParams.get('group') || 'All');
  const [filterProducts, setFilterProducts] = useState(() => new Set(searchParams.get('products')?.split(',').filter(Boolean) || []));
  const [filterPurities, setFilterPurities] = useState(() => new Set(searchParams.get('purities')?.split(',').filter(Boolean) || []));

  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [selectedEst, setSelectedEst] = useState(null);
  const [stockEditData, setStockEditData] = useState(null);
  const [catalogGroups, setCatalogGroups] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);

  // Duplicate form
  const [dupPhone, setDupPhone] = useState('');
  const [dupName, setDupName] = useState('');
  const [dupLoading, setDupLoading] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);

  const fetchEstimates = async (tab, force = false) => {
    const t = tab ?? activeTab;
    clearInterval(verifiedPollRef.current);
    if (t === 'verified') return loadVerified();
    const now = Date.now();
    const age = now - tabCacheTime.current[t];
    const hasCached = tabCache.current[t].length > 0;
    const isFresh = age < CACHE_TTL;

    // ── Cache hit: instant, no network, no spinner ──
    // (Bypass cache for search queries — always fetch fresh results)
    if (!force && hasCached && isFresh && !(t === 'active' && searchQuery)) {
      setEstimates(tabCache.current[t]);
      if (t === 'pending') setPendingCount(tabCache.current[t].length);
      return;
    }

    // First load → full spinner. Re-load → subtle bg indicator, keep stale data visible.
    if (!hasCached) {
      setLoading(true);
    } else {
      setBgRefreshing(true);
    }

    try {
      const params = new URLSearchParams();
      if (searchQuery && t === 'active') params.set('search', searchQuery);
      if (t === 'removed') params.set('status', 'removed');
      else if (t === 'pending') params.set('status', 'pending_approval');
      else params.set('status', 'active');
      const url = t === 'verified'
        ? '/stock-estimates/verified'
        : `/stock-estimates?${params.toString()}`;
      const res = await api.get(url);
      const data = res.data;
      tabCache.current[t] = data;
      tabCacheTime.current[t] = Date.now();
      // Tab may have changed while awaiting — don't clobber the newer tab's data.
      if (t !== activeTabRef.current) return;
      setEstimates(data);
      if (t === 'pending') setPendingCount(res.data.length);
    } catch (e) {
      console.error(e);
      // Keep stale data visible on network failure
      if (hasCached && t === activeTabRef.current) setEstimates(tabCache.current[t]);
    } finally {
      if (t === activeTabRef.current) {
        setLoading(false);
        setBgRefreshing(false);
      }
    }
  };

  // Verified tab — server prices every tag in the background; we show the saved snapshot and poll.
  const applyVerifiedSnapshot = (snap) => {
    // Wait-until-complete: rows are shown only once a check has fully finished.
    const ready = snap.status === 'done';
    if (activeTabRef.current !== 'verified') return;
    setEstimates(ready ? snap.rows : []);
    setReverifyTags(ready ? snap.reverify || [] : []);
    setVerifiedSnap(snap);
  };
  const pollVerified = async () => {
    const res = await api.get('/stock-estimates/verified/snapshot');
    applyVerifiedSnapshot(res.data);
    if (res.data.status !== 'running' || activeTabRef.current !== 'verified') clearInterval(verifiedPollRef.current);
    return res.data.status;
  };
  const loadVerified = async () => {
    setEstimates([]);
    setReverifyTags([]);
    setVerifiedSnap({ status: 'running', done: 0, total: 0 });
    try {
      await api.post('/stock-estimates/verified/refresh');
      if (activeTabRef.current !== 'verified') return;
      const status = await pollVerified();
      if (activeTabRef.current !== 'verified' || status !== 'running') return;
      clearInterval(verifiedPollRef.current);
      verifiedPollRef.current = setInterval(() => pollVerified().catch(() => {}), 3000);
    } catch (e) {
      toast.error('Could not load verified tags');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => () => clearInterval(verifiedPollRef.current), []);

  // Fetch pending count for the badge without loading the full pending tab
  const refreshPendingCount = async () => {
    if (!canApprove) return;
    try {
      const res = await api.get('/stock-estimates?status=pending_approval');
      setPendingCount(res.data.length);
      tabCache.current.pending = res.data;
      tabCacheTime.current.pending = Date.now();
    } catch (e) { /* non-critical */ }
  };

  useEffect(() => {
    // Initial load — only fire once. Catalog and pending badge fetched in parallel.
    fetchEstimates(activeTab);
    api.get('/catalog/main-groups').then(r => setCatalogGroups(r.data.map(i => i.name))).catch(() => {});
    api.get('/catalog/products').then(r => setCatalogProducts(r.data.map(i => i.name))).catch(() => {});
    if (canApprove) refreshPendingCount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync filters → URL silently
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const url = new URL(window.location.href);
    if (searchQuery) url.searchParams.set('q', searchQuery); else url.searchParams.delete('q');
    if (filterGroup && filterGroup !== 'All') url.searchParams.set('group', filterGroup); else url.searchParams.delete('group');
    if (filterProducts.size) url.searchParams.set('products', [...filterProducts].join(',')); else url.searchParams.delete('products');
    if (filterPurities.size) url.searchParams.set('purities', [...filterPurities].join(',')); else url.searchParams.delete('purities');
    window.history.replaceState(window.history.state, '', url.toString());
  }, [searchQuery, filterGroup, filterProducts, filterPurities]);

  // Refetch when search query changes (always fresh) or tab changes (respects cache).
  // Skip on mount — initial load is handled by useEffect([]) above.
  const isFirstMount = useRef(true);
  const lastFetchedTab = useRef(activeTab);
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    const tabChanged = lastFetchedTab.current !== activeTab;
    lastFetchedTab.current = activeTab;
    // Verified tab searches the already-loaded list client-side — typing must not restart the check.
    if (!tabChanged && activeTab === 'verified') return;
    const timer = setTimeout(() => fetchEstimates(activeTab), searchQuery && !tabChanged ? 300 : 0);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive unique filter options from loaded data (active tab only)
  const groupOptions = useMemo(() => {
    const groups = [...new Set(estimates.map(e => e.main_group || 'Plain').filter(Boolean))].sort();
    return ['All', ...groups];
  }, [estimates]);

  const productOptions = useMemo(() => {
    const set = filterGroup === 'All'
      ? estimates
      : estimates.filter(e => (e.main_group || 'Plain') === filterGroup);
    return [...new Set(set.map(e => e.product).filter(Boolean))].sort();
  }, [estimates, filterGroup]);

  const purityOptions = useMemo(() => {
    return [...new Set(estimates.map(e => e.gold_purity).filter(Boolean))].sort();
  }, [estimates]);

  // Apply all filters (active + verified tabs)
  const filteredEstimates = useMemo(() => {
    if (activeTab !== 'active' && activeTab !== 'verified') return estimates;
    const q = searchQuery.trim().toLowerCase();
    const list = estimates.filter(e => {
      if (filterGroup !== 'All' && (e.main_group || 'Plain') !== filterGroup) return false;
      if (filterProducts.size > 0 && !filterProducts.has(e.product)) return false;
      if (filterPurities.size > 0 && !filterPurities.has(e.gold_purity)) return false;
      // Verified tab searches client-side (the verified list is not server-filtered)
      if (activeTab === 'verified' && q) {
        const hay = [e.tag_number, e.main_group, e.product, e.gold_purity, e.counter_name]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    if (activeTab !== 'verified' || sortMode === 'tag') return list;
    const dir = sortMode === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      if (a.total_amount == null) return 1;
      if (b.total_amount == null) return -1;
      return dir * (a.total_amount - b.total_amount);
    });
  }, [estimates, filterGroup, filterProducts, filterPurities, activeTab, searchQuery, sortMode]);

  const verifiedTotalValue = useMemo(
    () => filteredEstimates.reduce((sum, e) => sum + (e.total_amount || 0), 0),
    [filteredEstimates],
  );

  const activeFilterCount = (filterGroup !== 'All' ? 1 : 0) + filterProducts.size + filterPurities.size;
  const verifiedWaiting = activeTab === 'verified' && ['running', 'error'].includes(verifiedSnap?.status);

  const clearAllFilters = () => {
    setFilterGroup('All');
    setFilterProducts(new Set());
    setFilterPurities(new Set());
  };

  const switchTab = (tab) => {
    if (tab === activeTab) return;
    setSelectMode(false);
    setSelectedIds(new Set());
    setActiveTab(tab);
    activeTabRef.current = tab;

    // INSTANT: show cached data in the same render cycle (before useEffect fires)
    // This eliminates any spinner flash on tab switches after first load.
    const cachedData = tabCache.current[tab];
    if (cachedData?.length > 0) {
      setEstimates(cachedData);
      setLoading(false);
      if (tab === 'pending') setPendingCount(cachedData.length);
    } else {
      // No cache yet — clear stale rows and show a spinner while fetching,
      // so the previous tab's items don't briefly render in the new tab's layout.
      setEstimates([]);
      setLoading(true);
    }
    // useEffect [activeTab] will background-refresh if cache is stale
  };

  // ── Multi-select helpers (active tab) ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(filteredEstimates.map(e => e._id)));
  const clearSelection = () => { setSelectedIds(new Set()); setSelectMode(false); };

  const openRemoveDialog = () => {
    if (selectedIds.size === 0) return;
    const items = filteredEstimates
      .filter(e => selectedIds.has(e._id))
      .map(e => ({ id: e._id, tag_number: e.tag_number, product: e.product, main_group: e.main_group, comment: '' }));
    setRemoveItems(items);
    setRemoveDialogOpen(true);
  };

  const handleBulkRemove = async () => {
    const missing = removeItems.filter(i => !i.comment.trim());
    if (missing.length > 0) {
      toast.error(`Comment required for: ${missing.map(i => i.tag_number).join(', ')}`);
      return;
    }
    setRemoving(true);
    try {
      const payload = { items: removeItems.map(i => ({ id: i.id, comment: i.comment.trim() })) };
      const res = await api.post('/stock-estimates/bulk-remove', payload);
      toast.success(`${res.data.removed_count} item(s) removed from stock`);
      setRemoveDialogOpen(false);
      clearSelection();
      // Invalidate both tabs so next visit fetches fresh data
      invalidateTab('active', 'removed');
      fetchEstimates('active', true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Removal failed');
    } finally {
      setRemoving(false);
    }
  };

  // ── Approval actions ──
  const handleApprove = async (est) => {
    // Optimistic: remove from pending list immediately
    const updatedPending = tabCache.current.pending.filter(e => e._id !== est._id);
    tabCache.current.pending = updatedPending;
    tabCacheTime.current.pending = Date.now();
    setEstimates(updatedPending);
    setPendingCount(Math.max(0, updatedPending.length));

    try {
      await api.patch(`/stock-estimates/${est._id}/approve`);
      toast.success(`Tag #${est.tag_number} approved and added to stock`);
      // Invalidate active so it picks up the newly approved item on next visit
      invalidateTab('active');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
      // Rollback: restore to pending
      invalidateTab('pending');
      fetchEstimates('pending', true);
    }
  };

  const openRejectDialog = (est) => {
    setRejectTarget(est);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setActionLoading(true);

    // Optimistic: remove from pending list immediately
    const updatedPending = tabCache.current.pending.filter(e => e._id !== rejectTarget._id);
    tabCache.current.pending = updatedPending;
    tabCacheTime.current.pending = Date.now();
    setEstimates(updatedPending);
    setPendingCount(Math.max(0, updatedPending.length));
    setRejectDialogOpen(false);

    try {
      await api.patch(`/stock-estimates/${rejectTarget._id}/reject`, { reason: rejectReason.trim() });
      toast.success(`Tag #${rejectTarget.tag_number} rejected`);
      setRejectTarget(null);
      setRejectReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
      // Rollback
      invalidateTab('pending');
      fetchEstimates('pending', true);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleProduct = (p) => {
    setFilterProducts(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  const togglePurity = (p) => {
    setFilterPurities(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  const openCreate = () => { setStockEditData(null); setFormOpen(true); };
  const openEdit = (est) => { setStockEditData(est); setFormOpen(true); };
  const openDetail = (est) => { setVerifiedMode(false); setSelectedEst(est); setDetailOpen(true); };

  // Verified tab — compute the full live price only when a tag is tapped open.
  const openVerifiedDetail = async (row) => {
    setVerifiedMode(true);
    if (row.preview) { setSelectedEst(row.preview); setDetailOpen(true); return; }
    setSelectedEst(null);
    setDetailOpen(true);
    setVerifiedLoading(true);
    try {
      const res = await api.get(`/stock-estimates/verified/${encodeURIComponent(row.tag_number)}/preview`);
      setSelectedEst(res.data);
      const patch = (list) => list.map(e => (e.tag_number === row.tag_number ? { ...e, total_amount: res.data.total_amount, preview: res.data } : e));
      tabCache.current.verified = patch(tabCache.current.verified);
      setEstimates(patch);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error(detail?.message || 'Tag inventory changed since verification — re-verify before estimating.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not load verified tag price');
      }
      setDetailOpen(false);
    } finally {
      setVerifiedLoading(false);
    }
  };
  const goldGate = useGoldRateGate();
  const openDuplicate = async (est) => {
    const ok = await goldGate.check();
    if (!ok) return; // gate popup shown + cashier notified
    setSelectedEst(est);
    setDupPhone('');
    setDupName('');
    setCustomerFound(false);
    setDuplicateOpen(true);
  };

  const handleDelete = async (est) => {
    if (!window.confirm(`Delete stock estimate ${est.tag_number}?`)) return;
    try {
      await api.delete(`/stock-estimates/${est._id}`);
      toast.success('Deleted');
      invalidateTab('active');
      fetchEstimates(activeTab, true);
    }
    catch (e) { toast.error('Failed'); }
  };

  const handleDupPhoneChange = async (phone) => {
    setDupPhone(phone);
    setCustomerFound(false);
    if (phone.length >= 10) {
      try {
        const customers = await customerService.searchByPhone(phone);
        if (customers.length > 0) {
          setDupName(customers[0].name);
          setCustomerFound(true);
        }
      } catch (e) { /* ignore */ }
    }
  };

  const handleDuplicate = async () => {
    if (!dupPhone.trim() || !dupName.trim()) { toast.error('Phone and name required'); return; }
    setDupLoading(true);
    try {
      const res = await api.post(`/stock-estimates/${selectedEst._id}/duplicate-to-customer`, {
        customer_phone: dupPhone.trim(),
        customer_name: dupName.trim(),
      });
      toast.success(`Customer estimate ${res.data.estimate_number} created for ${res.data.customer_name}`);
      setDuplicateOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setDupLoading(false); }
  };

  const handleShare = (est) => {
    const text = `*Stock Estimate — ${est.tag_number}*\n${est.main_group || ''} ${est.product || ''}\nGross: ${est.gross_weight}g | ${est.gold_purity}\nGold Rate: ${fmt(est.gold_rate)}/g\nGold: ${fmt(est.gold_amount)} | Stones: ${fmt(est.stones_amount)} | Making: ${fmt(est.making_amount)}\n*Total: ${fmt(est.total_amount)}* (incl. GST)`;
    if (navigator.share) {
      navigator.share({ title: `Estimate ${est.tag_number}`, text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    }
  };

  const openInBrowser = async (est) => {
    setPrintingEst(est);
    await new Promise(r => setTimeout(r, 150));
    if (!stockCardRef.current) return;
    try {
      const canvas = await html2canvas(stockCardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(`
          <html><head><title>Stock Estimate - ${est.tag_number || est.estimate_number}</title>
          <style>
            body { margin: 0; display: flex; justify-content: center; background: #f3f4f6; padding: 20px; }
            img { max-width: 100%; border-radius: 8px; box-shadow: 0 4px 24px rgba(0,0,0,0.12); }
            @media print { body { background: white; padding: 0; } img { box-shadow: none; max-width: 100%; } }
          </style>
          </head><body>
          <img src="${dataUrl}" />
          </body></html>
        `);
        win.document.close();
      }
    } catch (e) {
      toast.error('Could not generate preview');
    } finally {
      setPrintingEst(null);
    }
  };

  const tabLabel = activeTab === 'removed' ? 'Removed Stock' : activeTab === 'pending' ? 'Pending Approval' : activeTab === 'verified' ? 'Verified Tags' : 'Stock Estimates';
  const tabIcon = activeTab === 'removed' ? <PackageMinus className="w-5 h-5" /> : activeTab === 'pending' ? <Clock className="w-5 h-5" /> : activeTab === 'verified' ? <BadgeCheck className="w-5 h-5" /> : <Tag className="w-5 h-5" />;

  const tabSubtitle = () => {
    if (activeTab === 'removed') return `${estimates.length} removed item(s)`;
    if (activeTab === 'pending') return `${estimates.length} awaiting approval`;
    if (verifiedWaiting) return verifiedSnap.status === 'error' ? 'Price check failed' : 'Checking tags — totals will appear when ready';
    if (activeTab === 'verified') return activeFilterCount > 0 || searchQuery
      ? `${filteredEstimates.length} of ${estimates.length} verified tag(s) — tap for live price`
      : `${estimates.length} verified tag(s) — tap for live price`;
    return activeFilterCount > 0
      ? `${filteredEstimates.length} of ${estimates.length} tagged items — live pricing`
      : `${estimates.length} tagged items — live pricing`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="stock-estimates-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                {tabIcon} {tabLabel}
              </h1>
              <p className="text-xs text-emerald-200 flex items-center gap-1">
                {tabSubtitle()}
                {bgRefreshing && <RefreshCw className="w-3 h-3 animate-spin opacity-70" />}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Select mode controls — active tab + canRemove only */}
            {canRemove && activeTab === 'active' && (
              selectMode ? (
                <>
                  <span className="text-xs text-emerald-200">{selectedIds.size} selected</span>
                  <Button size="sm" variant="ghost" className="text-white hover:bg-emerald-700 text-xs" onClick={selectAll} data-testid="select-all-btn">All</Button>
                  <Button size="sm" variant="ghost" className="text-white hover:bg-red-600 text-xs" onClick={clearSelection} data-testid="cancel-select-btn"><X className="w-3 h-3 mr-1" />Cancel</Button>
                  {selectedIds.size > 0 && (
                    <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs h-8" onClick={openRemoveDialog} data-testid="remove-selected-btn">
                      <PackageMinus className="w-3 h-3 mr-1" /> Remove ({selectedIds.size})
                    </Button>
                  )}
                </>
              ) : (
                <Button size="sm" variant="ghost" className="text-white hover:bg-emerald-700 text-xs" onClick={() => setSelectMode(true)} data-testid="enter-select-mode-btn">
                  <CheckSquare className="w-4 h-4 mr-1" /> Select
                </Button>
              )
            )}
            {canCreate && activeTab === 'active' && (
              <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 h-8" onClick={openCreate} data-testid="create-stock-est-btn">
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            )}
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1.5 mt-3">
          <button
            onClick={() => switchTab('active')}
            data-testid="tab-active"
            className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-all ${
              activeTab === 'active' ? 'bg-white text-[#075E54]' : 'bg-emerald-700 text-emerald-100 hover:bg-emerald-600'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => switchTab('verified')}
            data-testid="tab-verified"
            className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center justify-center gap-1 ${
              activeTab === 'verified' ? 'bg-white text-[#075E54]' : 'bg-emerald-700 text-emerald-100 hover:bg-emerald-600'
            }`}
          >
            <BadgeCheck className="w-3.5 h-3.5" /> Verified
          </button>
          {canApprove && (
            <button
              onClick={() => switchTab('pending')}
              data-testid="tab-pending"
              className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                activeTab === 'pending' ? 'bg-white text-[#075E54]' : 'bg-emerald-700 text-emerald-100 hover:bg-emerald-600'
              }`}
            >
              Pending
              {pendingCount > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                  activeTab === 'pending' ? 'bg-amber-400 text-white' : 'bg-amber-400 text-white'
                }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => switchTab('removed')}
            data-testid="tab-removed"
            className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-all ${
              activeTab === 'removed' ? 'bg-white text-[#075E54]' : 'bg-emerald-700 text-emerald-100 hover:bg-emerald-600'
            }`}
          >
            Removed
          </button>
        </div>
      </div>

      {/* Search (active + verified tabs) */}
      {(activeTab === 'active' || (activeTab === 'verified' && !verifiedWaiting)) && (
        <div className="px-4 pt-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder={activeTab === 'verified' ? 'Search tag, product, group, purity...' : 'Search tag, product, group, stone details (VVS1, VS1, FG)...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="stock-est-search" />
          </div>
        </div>
      )}

      {/* Filter Chips (active + verified tabs) */}
      {(activeTab === 'active' || (activeTab === 'verified' && !verifiedWaiting)) && (
        <div className="px-4 pt-2 space-y-2 flex-shrink-0">
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
            {groupOptions.map(g => (
              <button
                key={g}
                onClick={() => { setFilterGroup(g); setFilterProducts(new Set()); }}
                data-testid={`filter-group-${g.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  filterGroup === g
                    ? 'bg-[#075E54] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          {productOptions.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
              {productOptions.map(p => (
                <button
                  key={p}
                  onClick={() => toggleProduct(p)}
                  data-testid={`filter-product-${p.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                    filterProducts.has(p)
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400 flex-shrink-0">Purity:</span>
            {purityOptions.map(p => (
              <button
                key={p}
                onClick={() => togglePurity(p)}
                data-testid={`filter-purity-${p.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium transition-all border ${
                  filterPurities.has(p)
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-amber-400'
                }`}
              >
                {p}
              </button>
            ))}
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                data-testid="clear-filters-btn"
                className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-red-500 border border-red-200 hover:bg-red-50 ml-auto"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {(activeTab === 'active' || activeTab === 'verified') && activeFilterCount > 0 && (
        <div className="px-4 pt-1">
          <p className="text-xs text-gray-400">Showing {filteredEstimates.length} of {estimates.length} items</p>
        </div>
      )}

      {activeTab === 'verified' && verifiedSnap && (
        <div className="mx-4 mt-2 text-xs" data-testid="verified-snapshot-status">
          {verifiedSnap.status === 'done' && verifiedSnap.finished_at ? (
            <p className="text-gray-500" data-testid="verified-prices-as-of">Prices as of {fmtSnapTime(verifiedSnap.finished_at)}</p>
          ) : null}
        </div>
      )}

      {/* Verified tab: total stock value + sort */}
      {activeTab === 'verified' && !loading && estimates.length > 0 && (
        <div className="mx-4 mt-2 rounded-lg bg-[#075E54] text-white px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="verified-total-value-bar">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-emerald-200">
              Total stock value · {filteredEstimates.length} tag(s){activeFilterCount > 0 || searchQuery.trim() ? ' (filtered)' : ''}
            </p>
            <p className="text-lg font-bold" data-testid="verified-total-value">{fmtLakh(verifiedTotalValue)}</p>
          </div>
          <div className="flex gap-1" data-testid="verified-sort">
            {[['tag', 'Tag no.'], ['desc', 'High → Low'], ['asc', 'Low → High']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setSortMode(k)}
                className={`text-[11px] px-2 py-1 rounded-full transition-colors ${sortMode === k ? 'bg-white text-[#075E54] font-semibold' : 'bg-emerald-700 text-emerald-100 hover:bg-emerald-600'}`}
                data-testid={`verified-sort-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pending tab info banner */}
      {activeTab === 'pending' && !loading && estimates.length > 0 && (
        <div className="mx-4 mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex-shrink-0">
          <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">These estimates were submitted by salesmen and are awaiting your approval before becoming visible in the active stock.</p>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-4 space-y-2">
        {activeTab === 'verified' && !loading && reverifyTags.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex gap-2" data-testid="verified-reverify-note">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <b>{reverifyTags.length} tag(s) need re-verification</b> (ERP stock changed since verification): {reverifyTags.join(', ')}
            </span>
          </div>
        )}
        {activeTab === 'verified' && verifiedSnap?.status === 'running' ? (
          <div className="flex flex-col items-center py-12 gap-3 px-6 text-center" data-testid="verified-wait-screen">
            <RefreshCw className="w-7 h-7 animate-spin text-emerald-600" />
            <p className="text-sm font-semibold text-gray-700" data-testid="verified-progress">
              Checking tags… {verifiedSnap.done || 0} of {verifiedSnap.total || '…'}
            </p>
            {verifiedSnap.total > 0 && (
              <div className="w-full max-w-xs h-1.5 rounded bg-emerald-100 overflow-hidden">
                <div className="h-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${Math.round((verifiedSnap.done / verifiedSnap.total) * 100)}%` }} />
              </div>
            )}
            <p className="text-xs text-gray-400">The list appears once every total is ready. You can leave this screen — checking continues on the server.</p>
          </div>
        ) : activeTab === 'verified' && verifiedSnap?.status === 'error' ? (
          <div className="flex flex-col items-center py-12 gap-3 text-center" data-testid="verified-snapshot-error">
            <AlertTriangle className="w-7 h-7 text-red-500" />
            <p className="text-sm text-red-600">Price check failed: {verifiedSnap.error}</p>
            <Button size="sm" onClick={() => loadVerified()} data-testid="verified-retry-check-btn">Try again</Button>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : filteredEstimates.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            {activeTab === 'removed' && <PackageMinus className="w-12 h-12 mx-auto mb-2 opacity-50" />}
            {activeTab === 'pending' && <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />}
            {activeTab === 'verified' && <BadgeCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />}
            {activeTab === 'active' && <Tag className="w-12 h-12 mx-auto mb-2 opacity-50" />}
            <p>
              {activeTab === 'removed' ? 'No removed items yet'
                : activeTab === 'pending' ? 'No estimates pending approval'
                : activeTab === 'verified' ? (activeFilterCount > 0 || searchQuery ? 'No verified tags match the filters' : 'No verified tags ready for estimation')
                : activeFilterCount > 0 ? 'No items match the selected filters'
                : 'No stock estimates yet'}
            </p>
            {(activeTab === 'active' || activeTab === 'verified') && activeFilterCount > 0 && (
              <button onClick={clearAllFilters} className="mt-2 text-xs text-emerald-600 underline">Clear filters</button>
            )}
          </div>
        ) : filteredEstimates.map(est => {
          const isSelected = selectedIds.has(est._id);

          // ── Verified Tag Card (read-only, live price on tap) ──
          if (activeTab === 'verified') {
            return (
              <div
                key={est.tag_number}
                className="bg-white rounded-lg border border-emerald-200 p-3 shadow-sm cursor-pointer hover:shadow-md hover:border-emerald-400 transition-all"
                onClick={() => openVerifiedDetail(est)}
                data-testid={`verified-tag-${est.tag_number}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div
                    className="relative w-24 h-24 rounded-lg overflow-hidden border bg-gray-100 flex-shrink-0"
                    onClick={(e) => { if (est.photo_url) { e.stopPropagation(); setPhotoViewUrl(photoSrc(est.photo_url)); } }}
                    data-testid={`verified-tag-photo-${est.tag_number}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ImageOff className="w-6 h-6 text-gray-300" />
                    </div>
                    {est.photo_url && (
                      <img src={photoSrc(est.photo_url)} alt={est.tag_number} loading="lazy" className="absolute inset-0 w-full h-full object-cover cursor-zoom-in" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-gray-900">{est.tag_number}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold flex items-center gap-0.5">
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                      {est.interim_tds && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold">Sell at TDS · Lab pending</span>
                      )}
                      {est.main_group && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{est.main_group}</span>}
                      {est.product && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{est.product}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                      <span>{est.gross_weight}g gross</span>
                      {est.gold_purity && <span>{est.gold_purity}</span>}
                      {est.gold_rate > 0 && <span>Rate: {fmt(est.gold_rate)}/g</span>}
                      {est.counter_name && <span className="text-emerald-600">{est.counter_name}</span>}
                    </div>
                    <div className="mt-1.5 text-base font-bold text-[#075E54]" data-testid={`verified-tag-total-${est.tag_number}`}>
                      {est.total_amount != null ? fmtLakh(est.total_amount) : (
                        <span className="text-xs font-normal text-gray-400" data-testid={`verified-tag-error-${est.tag_number}`}>
                          Total unavailable — tap to retry{est.price_error ? ` (${est.price_error})` : ''}
                        </span>
                      )}
                      {est.total_amount != null && <span className="ml-1 text-[10px] font-normal text-gray-400">incl. GST</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                </div>
              </div>
            );
          }

          // ── Pending Approval Card ──
          if (activeTab === 'pending') {
            return (
              <div
                key={est._id}
                className="bg-white rounded-lg border border-amber-200 p-3 shadow-sm"
                data-testid={`pending-est-${est.tag_number}`}
              >
                <div className="flex items-start justify-between">
                  {est.images?.length > 0 && (
                    <div className="relative w-14 h-14 rounded-lg overflow-hidden border flex-shrink-0 mr-3 bg-gray-100">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <ImageOff className="w-5 h-5 text-gray-300" />
                      </div>
                      <img src={est.images[0]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-gray-900">{est.tag_number}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-bold">{est.estimate_number}</span>
                      {est.main_group && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{est.main_group}</span>}
                      {est.product && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{est.product}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span>{est.gross_weight}g gross</span>
                      <span>{est.gold_purity}</span>
                      <span>Rate: {fmt(est.gold_rate)}/g</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Submitted by <span className="font-medium text-gray-600">{est.created_by_name}</span> · {est.created_at ? new Date(est.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className="font-bold text-lg text-emerald-700">{fmt(est.total_amount)}</p>
                    <p className="text-[10px] text-gray-400">incl. GST</p>
                  </div>
                </div>

                {/* Approve / Reject actions */}
                <div className="flex gap-2 mt-3 pt-3 border-t border-amber-100">
                  <Button
                    size="sm"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                    onClick={() => handleApprove(est)}
                    disabled={actionLoading}
                    data-testid={`approve-btn-${est.tag_number}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs"
                    onClick={() => openRejectDialog(est)}
                    disabled={actionLoading}
                    data-testid={`reject-btn-${est.tag_number}`}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            );
          }

          // ── Active / Removed Card ──
          return (
            <div
              key={est._id}
              className={`bg-white rounded-lg border p-3 transition-shadow relative ${
                selectMode
                  ? isSelected ? 'border-red-400 shadow-sm ring-1 ring-red-300 cursor-pointer' : 'border-gray-200 cursor-pointer hover:border-red-200'
                  : 'cursor-pointer hover:shadow-md'
              } ${activeTab === 'removed' ? 'opacity-80' : ''}`}
              onClick={() => selectMode ? toggleSelect(est._id) : openDetail(est)}
              data-testid={`stock-est-${est.tag_number}`}
            >
              {/* Select checkbox overlay */}
              {selectMode && (
                <div className="absolute top-2.5 right-2.5 z-10">
                  {isSelected
                    ? <CheckSquare className="w-5 h-5 text-red-500" />
                    : <Square className="w-5 h-5 text-gray-300" />}
                </div>
              )}
              <div className="flex items-start justify-between">
                {est.images?.length > 0 && (
                  <div className="relative w-14 h-14 rounded-lg overflow-hidden border flex-shrink-0 mr-3 bg-gray-100">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ImageOff className="w-5 h-5 text-gray-300" />
                    </div>
                    <img src={est.images[0]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-gray-900">{est.tag_number}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">{est.estimate_number}</span>
                    {est.main_group && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{est.main_group}</span>}
                    {est.product && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{est.product}</span>}
                    {activeTab === 'removed' && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-semibold">Removed</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    <span>{est.gross_weight}g gross</span><span>{est.gold_purity}</span><span>Rate: {fmt(est.gold_rate)}/g</span>
                  </div>
                  {/* Removal metadata */}
                  {activeTab === 'removed' && est.removal_comment && (
                    <div className="mt-1.5 bg-red-50 rounded px-2 py-1 border border-red-100">
                      <p className="text-[10px] text-red-700 font-medium leading-tight">"{est.removal_comment}"</p>
                      <p className="text-[9px] text-red-400 mt-0.5">
                        — {est.removed_by_name} · {est.removed_at ? new Date(est.removed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                      </p>
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="font-bold text-lg text-emerald-700">{fmt(est.total_amount)}</p>
                  <p className="text-[10px] text-gray-400">incl. GST</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Popup */}
      {photoViewUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPhotoViewUrl(null)}
          data-testid="verified-photo-viewer"
        >
          <button
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
            onClick={() => setPhotoViewUrl(null)}
            data-testid="verified-photo-viewer-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img src={photoViewUrl} alt="Product" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="stock-est-detail">
          {verifiedMode && verifiedLoading && !selectedEst && (
            <div className="py-12 flex flex-col items-center justify-center text-gray-400" data-testid="verified-detail-loading">
              <RefreshCw className="w-6 h-6 animate-spin text-emerald-600 mb-3" />
              <p className="text-sm">Calculating live price…</p>
            </div>
          )}
          {selectedEst && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-emerald-600" /> {selectedEst.tag_number}
                  {verifiedMode && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold flex items-center gap-0.5">
                      <BadgeCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  {selectedEst.estimate_number && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">{selectedEst.estimate_number}</span>}
                  {verifiedMode && selectedEst.interim_tds && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold">Sell at TDS · Lab pending</span>}
                  {selectedEst.main_group && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{selectedEst.main_group}</span>}
                  {selectedEst.product && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{selectedEst.product}</span>}
                  {verifiedMode && selectedEst.making_style_name && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded">MS: {selectedEst.making_style_name}</span>}
                </div>
                {selectedEst.images?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {selectedEst.images.map((img, i) => (
                      <div key={i} className="relative w-32 h-32 rounded-lg overflow-hidden border flex-shrink-0 bg-gray-100">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <ImageOff className="w-8 h-8 text-gray-300" />
                        </div>
                        <img src={img} alt={`Product ${i + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Gross Weight</span><span className="font-medium">{selectedEst.gross_weight}g</span></div>
                  {selectedEst.stones_weight_grams > 0 && <div className="flex justify-between"><span className="text-gray-500">Less Stones</span><span className="font-medium">-{selectedEst.stones_weight_grams?.toFixed(3)}g ({selectedEst.stones_weight_carats?.toFixed(2)}ct)</span></div>}
                  <div className="flex justify-between"><span className="text-gray-500">Net Weight</span><span className="font-medium">{selectedEst.net_weight?.toFixed(3)}g</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Wastage ({selectedEst.wastage_percentage}%)</span><span className="font-medium">{selectedEst.gold_weight_with_wastage?.toFixed(3)}g (with wastage)</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Gold Purity</span><span className="font-medium">{selectedEst.gold_purity}</span></div>
                  <div className="flex justify-between border-t pt-1.5"><span className="text-gray-500">Gold Rate</span><span className="font-bold text-emerald-700">{fmt(selectedEst.gold_rate)}/g</span></div>
                </div>
                <div className="bg-white border rounded-lg p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Gold Amount</span><span className="font-medium">{fmt(selectedEst.gold_amount)}</span></div>
                  {selectedEst.stones?.length > 0 && (
                    <>
                      <div className="flex justify-between"><span className="text-gray-500">Stones ({selectedEst.stones.length})</span><span className="font-medium">{fmt(selectedEst.stones_amount)}</span></div>
                      {selectedEst.stones.map((s, i) => (
                        <div key={i} className="flex justify-between text-xs text-gray-400 pl-3">
                          <span>{s.name || 'Stone'} — {s.weight}ct x Rs.{s.rate}</span><span>{fmt(s.amount)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="flex justify-between"><span className="text-gray-500">Making</span><span className="font-medium">{fmt(selectedEst.making_amount)}</span></div>
                  {selectedEst.other_charges > 0 && <div className="flex justify-between"><span className="text-gray-500">Other</span><span className="font-medium">{fmt(selectedEst.other_charges)}</span></div>}
                  <div className="flex justify-between border-t pt-1.5"><span className="text-gray-500">Subtotal</span><span className="font-medium">{fmt(selectedEst.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">GST (3%)</span><span className="font-medium">{fmt(selectedEst.gst_amount)}</span></div>
                  <div className="flex justify-between border-t pt-1.5 text-base"><span className="font-bold text-gray-800">Total</span><span className="font-bold text-emerald-700">{fmt(selectedEst.total_amount)}</span></div>
                </div>
              </div>
              <DialogFooter className="flex gap-2 flex-wrap">
                <Button variant="outline" className="flex-1" onClick={() => handleShare(selectedEst)} data-testid="share-stock-est-btn">
                  <Share2 className="w-4 h-4 mr-1" /> Share
                </Button>
                <Button variant="outline" className="flex-1 border-emerald-600 text-emerald-700 hover:bg-emerald-50" onClick={() => openInBrowser(selectedEst)} data-testid="open-browser-stock-est-btn" disabled={!!printingEst}>
                  <Printer className="w-4 h-4 mr-1" /> {printingEst ? 'Loading…' : 'Open & Print'}
                </Button>
                {!verifiedMode && (
                  <Button className="flex-1 bg-[#075E54] hover:bg-[#064E46]" onClick={() => { setDetailOpen(false); openDuplicate(selectedEst); }} data-testid="duplicate-stock-est-btn">
                    <Copy className="w-4 h-4 mr-1" /> Duplicate
                  </Button>
                )}
                {!verifiedMode && isAdmin && (
                  <>
                    <Button variant="outline" className="flex-shrink-0" onClick={() => { setDetailOpen(false); openEdit(selectedEst); }} data-testid="edit-stock-est-btn">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" className="flex-shrink-0 border-red-300 text-red-600 hover:bg-red-50" onClick={() => { setDetailOpen(false); handleDelete(selectedEst); }} data-testid="delete-stock-est-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate to Customer Dialog */}
      <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <DialogContent className="max-w-sm" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Duplicate to Customer Estimate</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="font-bold">{selectedEst?.tag_number}</p>
              <p className="text-gray-500">{selectedEst?.main_group} — {selectedEst?.product} — {fmt(selectedEst?.total_amount)}</p>
              <p className="text-[10px] text-gray-400 mt-1">Gold rate will be locked at current price</p>
            </div>
            <div className="space-y-1">
              <Label>Customer Phone *</Label>
              <PhoneInput
                value={normalizePhone(dupPhone)}
                onChange={val => handleDupPhoneChange(val)}
                inputProps={{ "data-testid": "dup-phone-input" }}
              />
            </div>
            <div className="space-y-1">
              <Label>Customer Name *</Label>
              <Input value={dupName} onChange={e => setDupName(e.target.value)} placeholder="Customer name" disabled={customerFound} data-testid="dup-name-input" />
              {customerFound && <p className="text-xs text-emerald-600">Existing customer found</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateOpen(false)}>Cancel</Button>
            <Button onClick={handleDuplicate} disabled={dupLoading} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="confirm-duplicate-btn">
              {dupLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              Create Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Stock Estimate */}
      <EstimateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSaved={() => {
          if (currentUser?.role !== 'admin') {
            toast.info('Estimate submitted for approval. You will be notified once approved.');
          }
          // Invalidate both active (admin bypass) and pending (salesman submit)
          invalidateTab('active', 'pending');
          fetchEstimates('active', true);
          if (canApprove) refreshPendingCount();
        }}
        isStockEstimate={true}
        stockEditData={stockEditData}
      />

      {/* Today's Gold Rate not-updated gate */}
      <GoldRateGateDialog
        {...goldGate.dialogProps}
        userRole={currentUser?.role}
        onUpdateNow={() => navigate('/gold-prices')}
      />

      {/* Bulk Remove Dialog */}
      <Dialog open={removeDialogOpen} onOpenChange={v => { if (!removing) setRemoveDialogOpen(v); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="remove-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <PackageMinus className="w-5 h-5" /> Remove from Stock
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">Each item requires a removal reason. These items will move to <strong>Removed Stock</strong>.</p>
            </div>
            {removeItems.map((item, idx) => (
              <div key={item.id} className="border rounded-lg p-3 space-y-2" data-testid={`remove-item-${item.tag_number}`}>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-gray-900">{item.tag_number}</span>
                  {item.main_group && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{item.main_group}</span>}
                  {item.product && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{item.product}</span>}
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500 font-medium">Removal reason <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. Sold to customer, Returned to supplier..."
                    value={item.comment}
                    onChange={e => setRemoveItems(prev => prev.map((it, i) => i === idx ? { ...it, comment: e.target.value } : it))}
                    className={`w-full text-sm border rounded-md px-3 py-1.5 outline-none focus:ring-1 focus:ring-red-400 ${!item.comment.trim() ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                    data-testid={`remove-comment-${item.tag_number}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialogOpen(false)} disabled={removing}>Cancel</Button>
            <Button
              onClick={handleBulkRemove}
              disabled={removing || removeItems.some(i => !i.comment.trim())}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-remove-btn"
            >
              {removing ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <PackageMinus className="w-4 h-4 mr-1" />}
              Confirm Removal ({removeItems.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={v => { if (!actionLoading) { setRejectDialogOpen(v); if (!v) { setRejectTarget(null); setRejectReason(''); } } }}>
        <DialogContent className="max-w-sm" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" /> Reject Estimate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {rejectTarget && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-bold">{rejectTarget.tag_number}</p>
                <p className="text-gray-500">{rejectTarget.main_group} — {rejectTarget.product}</p>
                <p className="text-[10px] text-gray-400 mt-1">Submitted by {rejectTarget.created_by_name}</p>
              </div>
            )}
            <div className="space-y-1">
              <Label>Reason for rejection <span className="text-red-500">*</span></Label>
              <Input
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="e.g. Incorrect weight, duplicate tag..."
                data-testid="reject-reason-input"
                autoFocus
              />
              <p className="text-[10px] text-gray-400">The salesman will be notified with this reason.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectTarget(null); setRejectReason(''); }} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-reject-btn"
            >
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Off-screen card for html2canvas rendering */}
      {printingEst && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1 }}>
          <StockEstimateCard ref={stockCardRef} estimate={printingEst} />
        </div>
      )}
    </div>
  );
};

export default StockEstimates;
